import { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, FileText, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/**
 * Full self-service section the logged-in user sees in their profile.
 * Fetches their personal acceptance status from the backend and renders
 * a card with per-document timestamp + version. Doubles as a transparency
 * proof for support cases ("haben Sie überhaupt zugestimmt?").
 */
export default function LegalAcceptanceStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/legal-status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!status) return null;

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-3" data-testid="legal-status-section">
      <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-[#00E575]" size={18} />
          <h2 className="font-display text-lg font-semibold">Rechtsdokumente</h2>
        </div>
        {status.has_missing_acceptances ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[12px] font-semibold uppercase tracking-wider">
            <AlertTriangle size={11} /> Zustimmung ausstehend
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[12px] font-semibold uppercase tracking-wider">
            <ShieldCheck size={11} /> Alle akzeptiert
          </span>
        )}
      </div>

      <div className="space-y-2">
        {(status.documents || []).map((d) => (
          <div
            key={d.slug}
            className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#09090B] border border-zinc-800"
            data-testid={`legal-status-doc-${d.slug}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-zinc-100 font-medium flex items-center gap-2 flex-wrap">
                <FileText size={13} className="text-zinc-500 shrink-0" />
                {d.title}
                <span className="text-[12px] text-zinc-300 font-mono">v{d.current_version}</span>
              </div>
              <div className="text-xs mt-1 ml-5">
                {d.status === "current" && (
                  <span className="text-emerald-400">
                    ✓ Akzeptiert am {fmt(d.accepted_at)}
                  </span>
                )}
                {d.status === "outdated" && (
                  <span className="text-amber-400">
                    ⚠ Neue Version v{d.current_version} verfügbar — letzte Zustimmung
                    (v{d.accepted_version}) am {fmt(d.accepted_at)}
                  </span>
                )}
                {d.status === "missing" && (
                  <span className="text-rose-400">✗ Noch nicht akzeptiert</span>
                )}
              </div>
            </div>
            <Link
              to={`/legal/${d.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-200 transition shrink-0"
              data-testid={`legal-status-open-${d.slug}`}
            >
              Lesen <ExternalLink size={11} />
            </Link>
          </div>
        ))}
      </div>

      <p className="text-[12px] text-zinc-300 mt-3 leading-relaxed">
        Deine Zustimmungen werden mit Zeitstempel, IP-Adresse und Versions-Hash
        revisionssicher protokolliert (DSGVO Art. 7).
      </p>
    </div>
  );
}

/**
 * Compact inline summary used in admin user lists. Renders one row per
 * accepted document with the acceptance date. Designed to fit inside
 * an existing card without extra styling.
 */
export const AdminLegalAcceptanceSummary = ({ acceptances = [] }) => {
  if (!Array.isArray(acceptances) || acceptances.length === 0) {
    return (
      <div className="text-xs text-amber-400 mt-2 flex items-center gap-1.5"
           data-testid="admin-legal-none">
        <AlertTriangle size={11} /> Rechtsdokumente noch nicht akzeptiert
      </div>
    );
  }
  return (
    <div className="text-xs text-zinc-400 mt-2 space-y-0.5" data-testid="admin-legal-list">
      {acceptances.map((a) => (
        <div key={a.slug} className="flex items-center gap-1.5">
          <ShieldCheck size={11} className="text-emerald-400 shrink-0" />
          <span className="uppercase tracking-wider text-emerald-400 font-semibold">{a.slug}</span>
          <span className="text-zinc-500">v{a.version}</span>
          <span className="text-zinc-400">· {fmt(a.accepted_at)}</span>
          {a.ip && <span className="text-zinc-600 font-mono">· {a.ip}</span>}
        </div>
      ))}
    </div>
  );
};
